import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

interface DetectionResult {
  is_scam: boolean;
  confidence: number;
  flags: string[];
  reason?: string;
}

interface FilePreview {
  file: File;
  previewUrl: string;
}

type VerdictTier = 'danger' | 'caution' | 'safe';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_NARRATIVE_LENGTH = 2000;

const EXAMPLES = [
  {
    label: 'Try: delivery fee text',
    text: 'A customer said they got a text from "our store" asking them to pay a $3.99 late delivery fee through a link.',
  },
  {
    label: 'Try: fake IT call',
    text: 'I got a call from someone claiming to be corporate IT, asking me to install remote access software to fix the register.',
  },
];

function getVerdictTier(result: DetectionResult): VerdictTier {
  if (result.is_scam) return 'danger';
  return result.confidence >= 0.6 ? 'safe' : 'caution';
}

const VERDICT_COPY: Record<VerdictTier, { label: string; icon: string }> = {
  danger: { label: 'Likely a scam', icon: 'ti-alert-triangle' },
  caution: { label: "Inconclusive — we're not sure", icon: 'ti-help-circle' },
  safe: { label: 'Looks safe', icon: 'ti-circle-check' },
};

interface WaitlistState {
  open: boolean;
  confirmed: boolean;
}

export const CheckSomething = () => {
  const [text, setText] = useState('');
  const [result, setResult] = useState<DetectionResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<FilePreview[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [waitlist, setWaitlist] = useState<Record<'win' | 'android', WaitlistState>>({
    win: { open: false, confirmed: false },
    android: { open: false, confirmed: false },
  });

  useEffect(() => {
    return () => {
      files.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFiles = (incoming: File[]) => {
    if (incoming.length === 0) return;

    if (files.length + incoming.length > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    const validFiles: FilePreview[] = [];
    for (const file of incoming) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        setError(`Invalid file type: ${file.name}. Only PNG, JPG, GIF, and WebP are allowed.`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(`File too large: ${file.name}. Maximum size is 10MB.`);
        return;
      }
      validFiles.push({ file, previewUrl: URL.createObjectURL(file) });
    }

    setFiles((prev) => [...prev, ...validFiles]);
    setError(null);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(Array.from(event.target.files || []));
    event.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const openFileDialog = () => fileInputRef.current?.click();

  const handleDropzoneKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openFileDialog();
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    handleFiles(Array.from(event.dataTransfer.files || []));
  };

  const handleExampleClick = (exampleText: string) => {
    setText(exampleText);
  };

  const handleCheck = async () => {
    if (!text.trim() && files.length === 0) return;

    setLoading(true);
    setResult(null);
    setCheckError(null);

    try {
      let response: Response;

      if (files.length > 0) {
        const formData = new FormData();
        if (text.trim()) {
          formData.append('text', text);
        }
        files.forEach(({ file }) => {
          formData.append('files', file);
        });

        response = await fetch('/api/v1/detect', {
          method: 'POST',
          body: formData,
        });
      } else {
        response = await fetch('/api/v1/detect', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ text }),
        });
      }

      if (!response.ok) {
        throw new Error('Failed to check text');
      }

      const data = await response.json();
      setResult(data);
      files.forEach((f) => URL.revokeObjectURL(f.previewUrl));
      setFiles([]);
    } catch (err) {
      console.error('Error checking text:', err);
      // Never fabricate a verdict on failure — a security tool that guesses
      // "scam" (or "safe") when its real analysis didn't run is worse than
      // no verdict at all. Show a real error and let the user retry.
      setCheckError('Something went wrong checking this message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openWaitlist = (target: 'win' | 'android') => {
    setWaitlist((prev) => ({ ...prev, [target]: { ...prev[target], open: true } }));
  };

  const submitWaitlist = (target: 'win' | 'android', event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setWaitlist((prev) => ({ ...prev, [target]: { open: false, confirmed: true } }));
  };

  const tier = result ? getVerdictTier(result) : null;

  return (
    <div className="check-page">
      <section className="hero" id="top">
        <h1>Is this a scam?</h1>
        <p className="lede">
          Describe what happened or upload a screenshot below — it's free, it's fast, and you don't need an
          account.
        </p>
      </section>

      <div className="console-wrap">
        <div className="console">
          <div className="console-head">
            <span className="live-dot"></span>
            <span>Try it now</span>
            <span className="sub">— takes about 10 seconds</span>
          </div>

          <div className="console-body">
            <div className="console-pane">
              <label className="pane-label" htmlFor="narrative">
                <span className="step-chip">1</span> Describe what happened
              </label>
              <div className="examples">
                {EXAMPLES.map((example) => (
                  <button
                    key={example.label}
                    type="button"
                    className="example-chip"
                    onClick={() => handleExampleClick(example.text)}
                  >
                    {example.label}
                  </button>
                ))}
              </div>
              <textarea
                id="narrative"
                maxLength={MAX_NARRATIVE_LENGTH}
                placeholder="Click a suggestion above, or start typing here…"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <span className="charcount">
                {text.length}/{MAX_NARRATIVE_LENGTH}
              </span>
            </div>

            <div className="console-pane">
              <label className="pane-label" htmlFor="file-input">
                <span className="step-chip">2</span> Upload a screenshot or photo
              </label>
              <div
                className={`dropzone${dragActive ? ' drag' : ''}`}
                role="button"
                tabIndex={0}
                aria-label="Upload a screenshot or photo"
                onClick={openFileDialog}
                onKeyDown={handleDropzoneKeyDown}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                }}
                onDrop={handleDrop}
              >
                <i className="ti ti-cloud-upload" aria-hidden="true"></i>
                <span className="primary">
                  Drag and drop, or <span className="browse-btn">browse files</span>
                </span>
                <span className="secondary">PNG, JPG, GIF, or WebP — up to 10MB each, up to 5 files</span>
                <input
                  ref={fileInputRef}
                  id="file-input"
                  type="file"
                  accept={ACCEPTED_TYPES.join(',')}
                  multiple
                  onChange={handleFileInputChange}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {files.length > 0 && (
                <div className="file-preview-grid">
                  {files.map((preview, index) => (
                    <div key={index} className="file-preview-item">
                      <img src={preview.previewUrl} alt={preview.file.name} />
                      <div className="file-preview-info">
                        <span className="file-name">{preview.file.name}</span>
                        <span className="file-size">{(preview.file.size / 1024).toFixed(1)} KB</span>
                      </div>
                      <button
                        className="file-remove-btn"
                        onClick={() => handleRemoveFile(index)}
                        type="button"
                        aria-label={`Remove ${preview.file.name}`}
                      >
                        <i className="ti ti-x" aria-hidden="true"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {error && (
                <div className="error-message">
                  <i className="ti ti-alert-circle" aria-hidden="true"></i>
                  {error}
                </div>
              )}
            </div>
          </div>

          <div className="console-footer">
            <p className="disclaimer">
              We review this to generate a result. Don't include payment details or full account numbers.
            </p>
            <button
              type="button"
              className="primary-cta"
              onClick={handleCheck}
              disabled={loading || (!text.trim() && files.length === 0)}
            >
              {loading ? 'Checking…' : 'Check this message'}
              <i className="ti ti-arrow-right" aria-hidden="true"></i>
            </button>
          </div>

          {(loading || result || checkError) && (
            <div className="verdict open" aria-live="polite">
              {loading ? (
                <div className="verdict-row">
                  <span className="verdict-dot"></span>
                  <span>Analyzing…</span>
                </div>
              ) : !result || !tier ? (
                <div className="verdict-row tier-error">
                  <i className="ti ti-alert-circle" aria-hidden="true"></i>
                  <span>{checkError || 'Something went wrong checking this message. Please try again.'}</span>
                </div>
              ) : (
                <>
                  <div className={`verdict-row tier-${tier}`}>
                    <i className={`ti ${VERDICT_COPY[tier].icon}`} aria-hidden="true"></i>
                    <span>{VERDICT_COPY[tier].label}</span>
                  </div>
                  <p className="verdict-note">
                    {result.reason ||
                      (tier === 'safe'
                        ? 'No obvious signs of fraud or phishing in this message.'
                        : 'This looks suspicious.')}{' '}
                    {result.flags.length > 0 && <>Detected: {result.flags.join(', ')}. </>}
                    Always verify requests through known channels before taking action.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Privacy / trust section */}
      <section className="dark-section" id="trust">
        <div className="dark-inner">
          <div className="badge-icon badge-teal">
            <i className="ti ti-shield-check" aria-hidden="true"></i>
          </div>
          <h2>Your Privacy Matters</h2>
          <p className="body-copy">
            We analyze the message you paste, not you. No account, no name, no email required. Suspicious phone
            numbers, domains, and message patterns may be shared anonymously with fraud prevention teams. Your
            identity is never part of that.
          </p>

          <div className="trust-grid">
            <div className="trust-item">
              <div className="badge-icon badge-blue">
                <i className="ti ti-lock" aria-hidden="true"></i>
              </div>
              <h3>No account needed</h3>
              <p>Check instantly without signing up or logging in.</p>
            </div>
            <div className="trust-item">
              <div className="badge-icon badge-teal">
                <i className="ti ti-user-x" aria-hidden="true"></i>
              </div>
              <h3>No personal data stored</h3>
              <p>We never link a submission to who you are.</p>
            </div>
            <div className="trust-item">
              <div className="badge-icon badge-green">
                <i className="ti ti-shield-check" aria-hidden="true"></i>
              </div>
              <h3>Scam patterns only</h3>
              <p>Only suspicious numbers, domains, and tactics are shared — anonymized.</p>
            </div>
          </div>

          <p className="policy-link">
            View our complete <Link to="/privacy">Privacy Policy</Link> for details.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="dark-section" id="how-it-works" style={{ paddingTop: 0 }}>
        <div className="dark-inner">
          <h2>How It Works</h2>
          <p className="body-copy">Three steps. No account. No expertise required.</p>

          <div className="steps-grid">
            <div className="step-card">
              <div className="step-num n1">1</div>
              <h3>Submit Your Message</h3>
              <p>
                Paste any suspicious text, email, or message. Use natural language — it's as easy as using a
                search engine. Include phone numbers, links, or describe what you received.
              </p>
            </div>
            <div className="step-card">
              <div className="step-num n2">2</div>
              <h3>Get Instant Answers</h3>
              <p>
                Get a likelihood score and clear explanation of why a message looks suspicious, plus specific
                red flags detected and guidance on what to do next. No guessing, no waiting.
              </p>
            </div>
            <div className="step-card">
              <div className="step-num n3">3</div>
              <h3>Help Fight Fraud</h3>
              <p>
                Suspicious phone numbers, domains, and message patterns are anonymized and shared with fraud
                analysts to disrupt scam operations before more victims lose money.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stage diagram */}
      <section className="dark-section" style={{ paddingTop: 0 }}>
        <div className="dark-inner">
          <h2>Why Our Scam Checker Is Different</h2>
          <p className="body-copy">
            Other scam checkers analyze your message and stop. Ours contributes what it finds to a fraud
            intelligence database used by analysts to recognize active scam campaigns and help stop money from
            leaving victims' accounts.
          </p>

          <div className="diagram">
            <div className="diagram-stage">
              <span className="stage-label">Stage 1</span>
              <div className="node violet">
                <i className="ti ti-user" aria-hidden="true"></i>
              </div>
              <div className="node-title">You</div>
              <div className="node-sub">Suspicious message</div>
            </div>

            <div className="diagram-connector">
              <span>anonymized</span>
              <span className="line"></span>
            </div>

            <div className="diagram-stage">
              <span className="stage-label">Stage 2</span>
              <div className="node teal">
                <i className="ti ti-shield-check" aria-hidden="true"></i>
              </div>
              <div className="node-title">Scam Checker</div>
              <div className="node-sub">Detection + intelligence</div>
            </div>

            <div className="diagram-connector">
              <span>processed intel</span>
              <span className="line"></span>
            </div>

            <div className="diagram-stage">
              <span className="stage-label">Stage 3</span>
              <div className="node blue">
                <i className="ti ti-search" aria-hidden="true"></i>
              </div>
              <div className="node-title">Fraud Analysts</div>
              <div className="node-sub">Investigate campaigns</div>
            </div>

            <div className="diagram-connector">
              <span></span>
              <span className="line"></span>
            </div>

            <div className="diagram-actions">
              <span className="stage-label" style={{ alignSelf: 'center' }}>
                Action
              </span>
              <div className="action-node">
                <span className="action-dot dot-blue"></span>Bank flags transaction
              </div>
              <div className="action-node">
                <span className="action-dot dot-teal"></span>Credit union notified
              </div>
              <div className="action-node">
                <span className="action-dot dot-green"></span>Fraud team investigates
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="capabilities" id="capabilities">
        <div className="capabilities-head">
          <h2>More ways we keep you covered</h2>
          <p>Everything here lives under one account, whether it's ready today or on its way.</p>
        </div>

        <div className="cap-grid">
          <article className="cap-card">
            <div className="cap-top">
              <div className="cap-icon">
                <i className="ti ti-message-check" aria-hidden="true"></i>
              </div>
              <span className="tag live">Live</span>
            </div>
            <h3 className="cap-title">Scam Checker</h3>
            <p className="cap-desc">
              Check a message, call script, or screenshot in seconds. The tool above — free, no account required.
            </p>
            <div className="cap-foot">
              <span className="you">↑ you're using this now</span>
            </div>
          </article>

          <WaitlistCard
            target="win"
            icon="ti-brand-windows"
            title="Windows Protection"
            desc="Real-time protection for your PC, running quietly in the background."
            state={waitlist.win}
            onOpen={() => openWaitlist('win')}
            onSubmit={(e) => submitWaitlist('win', e)}
          />

          <WaitlistCard
            target="android"
            icon="ti-brand-android"
            title="Android Protection"
            desc="Keep your phone covered, wherever calls and texts are coming from."
            state={waitlist.android}
            onOpen={() => openWaitlist('android')}
            onSubmit={(e) => submitWaitlist('android', e)}
          />
        </div>
      </section>
    </div>
  );
};

interface WaitlistCardProps {
  target: string;
  icon: string;
  title: string;
  desc: string;
  state: WaitlistState;
  onOpen: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}

const WaitlistCard = ({ target, icon, title, desc, state, onOpen, onSubmit }: WaitlistCardProps) => (
  <article className="cap-card dim">
    <div className="cap-top">
      <div className="cap-icon">
        <i className={`ti ${icon}`} aria-hidden="true"></i>
      </div>
      <span className="tag soon">Soon</span>
    </div>
    <h3 className="cap-title">{title}</h3>
    <p className="cap-desc">{desc}</p>
    <div className="cap-foot waitlist">
      {!state.open && !state.confirmed && (
        <button className="waitlist-btn" type="button" onClick={onOpen}>
          Join the waitlist
        </button>
      )}
    </div>
    {state.open && (
      <form className="waitlist-form open" onSubmit={onSubmit}>
        <label className="sr-only" htmlFor={`waitlist-email-${target}`}>
          Email address
        </label>
        <input id={`waitlist-email-${target}`} type="email" placeholder="you@email.com" required />
        <button className="waitlist-btn" type="submit">
          Notify me
        </button>
      </form>
    )}
    {state.confirmed && (
      <p className="waitlist-confirm open">You're on the list — we'll email you when it's ready.</p>
    )}
  </article>
);
